import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAudit } from '../context/AuditContext';
import { auditPath } from '../utils/auditRoutes';
import { PageFrame } from '../components/PageFrame';
import { SectionCard } from '../components/SectionCard';
import { RatingGroup } from '../components/RatingGroup';
import { TechStackSection } from '../components/TechStackSection';
import { NavigationControls } from '../components/NavigationControls';
import { SectionRecapCard } from '../components/SectionRecapCard';
import { Megaphone, BarChart3, Laptop, Lightbulb } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const questions = [
  'Our salespeople spend significant time on non-revenue-generating activities (admin, data entry, lead enrichment, and reporting).',
  'Sales reps struggle to efficiently manage customer lifecycle, causing deals to stall or fall through.',
  'Creating content (blogs, social posts, emails, etc.) takes too much time.',
  'We struggle to track which marketing activities actually drive ROI or sales results.',
  'Managing asset updates across multiple channels (website, email, social) is fragmented and repetitive.'
];

export default function MarketingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditData, updateSectionRating, updateSectionNotes } = useAudit();
  const section = auditData.sections.marketing;
  const handleNext = () => {
    navigate(auditPath('/team', location.pathname));
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  return (
    <PageFrame pageNumber={5} backgroundStyle="light">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="bg-primary text-white rounded-lg p-6 text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-wide w-full sm:w-auto mx-auto" data-testid="section-title">
              Section 3 – Sales & Marketing
            </h1>
          </div>

          <SectionCard
            title="Rate Each Statement"
            description="How true is each statement for your business?"
            icon={Megaphone}
          >
            <div className="space-y-8">
              {questions.map((question, index) => (
                <div key={index} className="pb-6 border-b border-muted last:border-0">
                  <RatingGroup
                    value={section.ratings[index]}
                    onChange={(rating) => updateSectionRating('marketing', index, rating)}
                    questionId={`marketing-q${index + 1}`}
                    label={`${index + 1}. ${question}`}
                  />
                </div>
              ))}
            </div>
          </SectionCard>

          <div className="flex items-center justify-between bg-muted rounded-lg p-4 mt-4">
            <span className="text-secondary font-semibold">Section Total:</span>
            <span className="text-3xl font-bold text-primary" data-testid="marketing-total">
              {section.total} <span className="text-base text-muted-foreground">/ 25</span>
            </span>
          </div>

          <SectionRecapCard
            sectionKey="marketing"
            section={section}
            tools={auditData.tech_stack?.salesMarketing?.tools || []}
            integrationRating={auditData.tech_stack?.salesMarketing?.integration_rating || 0}
          />

          <div className="bg-primary text-white rounded-lg p-6 mt-6">
            <div className="flex items-start gap-4">
              <Lightbulb className="h-8 w-8 flex-shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <h3 className="font-bold text-lg mb-2">Insight</h3>
                <p>
                  High scores reveal opportunities for AI content generation, automating non-revenue generating work for sales teams, predictive analytics, and intelligent campaign optimization.
                </p>
              </div>
            </div>
          </div>

          <SectionCard title="Reflection" icon={BarChart3} className="mt-6">
            <div className="space-y-2">
              <Label htmlFor="marketing-reflection">Which part of sales and marketing feels most manual or time-consuming? (Optional)</Label>
              <Textarea
                id="marketing-reflection"
                value={section.notes}
                onChange={(e) => updateSectionNotes('marketing', e.target.value)}
                placeholder="Describe your biggest sales and marketing bottleneck..."
                className="min-h-32 resize-y"
                data-testid="marketing-reflection-textarea"
              />
              <p className="text-xs text-muted-foreground">Optional. More detail helps us personalize the report.</p>
            </div>
          </SectionCard>

          <div className="mt-6">
            <TechStackSection category="salesMarketing" />
          </div>

          <div className="pt-4">
            <NavigationControls currentPage={5} customNext={handleNext} />
          </div>
        </div>
      </div>
    </PageFrame>
  );
}